import React, {
  useCallback,
  useEffect,
  useState,
} from 'react';
import cx from 'classnames';
import type {
  Gallery,
  YearItem,
} from 'types';
import { RequestStatus } from 'types';
import { useRequest } from 'hooks';
import {
  Preloader,
  NoData,
  ButtonV4,
} from 'components';
import {
  URL,
  routes,
} from 'appConstants';
import {
  _t,
  _t_back,
  _has_t_back,
  dateToUiFormat,
} from 'utils';

import iconArrow from 'assets/img/resolutions-v4/icon-arrow.svg';

import MediaShell from '../MediaShell';
import newsStyles from '../MediaNews/styles.module.scss';
import styles from './styles.module.scss';

const MediaGallery = () => {
  const [yearID, setYearID] = useState<number>(0);

  const {
    data: years,
    request: yearsReq,
  } = useRequest<YearItem[]>();

  useEffect(() => {
    yearsReq({
      method: 'get',
      url: URL.GALLERY.YEARS,
    });
  }, []);

  useEffect(() => {
    if (years?.length && !yearID) {
      setYearID(years[0].id);
    }
  }, [years, yearID]);

  const changeYear = useCallback(({ currentTarget }: React.MouseEvent<HTMLButtonElement>) => {
    setYearID(+currentTarget.id);
  }, []);

  const {
    data: gallery,
    status: galleryStat,
    request: galleryReq,
  } = useRequest<Gallery[]>();

  useEffect(() => {
    if (!yearID) {
      return;
    }
    galleryReq({
      method: 'get',
      url: URL.GALLERY.GET,
      params: {
        category_gallery_date: yearID,
      },
    });
  }, [yearID]);

  const filteredGallery = (gallery || []).filter((item) => (
    _has_t_back(item) && (item.pictures?.[0] || item.video)
  ));

  return (
    <MediaShell
      title="Галерея"
      crumbCurrent="Галерея"
    >
      {years?.length ? (
        <div className={styles.yearsBar}>
          {years.map((item) => (
            <button
              key={item.id}
              type="button"
              id={`${item.id}`}
              className={cx(styles.yearBtn, yearID === item.id && styles.yearBtnActive)}
              onClick={changeYear}
            >
              {item.year}
            </button>
          ))}
        </div>
      ) : null}

      <section className={newsStyles.list}>
        <Preloader isLoading={galleryStat === RequestStatus.REQUEST}>
          <NoData count={filteredGallery.length || 0}>
            <ul className={newsStyles.grid}>
              {filteredGallery.map((item, index) => {
                const image = item.pictures?.[0]?.image;
                const featured = index === 0;
                const withImage = Boolean(image) && !featured;

                return (
                  <li
                    key={item.id}
                    className={cx(
                      newsStyles.card,
                      featured && newsStyles.cardFeatured,
                      withImage && newsStyles.cardImage,
                    )}
                    style={withImage ? {
                      ['--news-photo' as string]: `url(${image})`,
                    } : undefined}
                  >
                    <span className={newsStyles.badge}>{_t('ГАЛЕРЕЯ')}</span>
                    <div className={newsStyles.cardBody}>
                      <time className={newsStyles.date} dateTime={item.date}>
                        {dateToUiFormat(item.date)}
                      </time>
                      <h2 className={newsStyles.cardTitle}>{_t_back(item, 'title')}</h2>
                      <ButtonV4
                        to={`${routes.media.gallery.root}/${item.id}`}
                        label="Подробнее"
                        variant="outline"
                        tone={(featured || withImage) ? 'onDark' : 'onLight'}
                        className={newsStyles.detailBtn}
                        iconSrc={iconArrow}
                      />
                    </div>
                  </li>
                );
              })}
            </ul>
          </NoData>
        </Preloader>
      </section>
    </MediaShell>
  );
};

export default MediaGallery;
